import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '@/hooks/useNotifications';
import type { NotificationItem } from '@/services/api';

function formatTimeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function getNotificationMessage(n: NotificationItem): string {
  const name = n.actor?.display_name || n.actor?.username || 'Someone';
  const MESSAGE_MAP: Record<NotificationItem['notification_type'], string> = {
    comment: `${name} commented on your scorecard`,
    reply: `${name} replied to your comment`,
    like: `${name} liked your scorecard`,
    follow: `${name} started following you`,
  };
  return MESSAGE_MAP[n.notification_type] ?? `${name} interacted with you`;
}

function getNotificationLink(n: NotificationItem): string {
  // Flagged comment notifications take admins to the moderation queue
  if (n.preview_text?.startsWith('[FLAGGED]')) {
    return '/admin/flagged-comments';
  }
  if (n.notification_type === 'follow' && n.actor) {
    return `/scorecards/user/${n.actor.username || n.actor.id}`;
  }
  if (n.scorecard_id) {
    const hash =
      n.notification_type === 'comment' || n.notification_type === 'reply' ? '#comments' : '';
    return `/scorecards/${n.scorecard_id}${hash}`;
  }
  return '/notifications';
}

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications(10, 0);
  const markAllRead = useMarkAllNotificationsRead();
  const markOneRead = useMarkNotificationRead();

  const notifications = data?.notifications ?? [];
  const hasUnread = notifications.some((n) => !n.is_read);

  const handleClick = (n: NotificationItem) => {
    if (!n.is_read) {
      markOneRead.mutate(n.id);
    }
    onClose();
    navigate(getNotificationLink(n));
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  return (
    <div
      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
      style={{ fontFamily: "'Source Sans 3', sans-serif" }}
      role="menu"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-900">Notifications</span>
        {hasUnread && (
          <button
            onClick={handleMarkAllRead}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 flex items-start gap-3 ${
                !n.is_read ? 'bg-blue-50/40' : ''
              }`}
              role="menuitem"
            >
              {/* Avatar */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 overflow-hidden mt-0.5">
                {n.actor?.avatar_url ? (
                  <img src={n.actor.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-bold">
                    {(n.actor?.display_name || n.actor?.username || '?')[0]?.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 leading-snug">{getNotificationMessage(n)}</p>
                {n.preview_text && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">"{n.preview_text}"</p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">{formatTimeAgo(n.created_at)}</p>
              </div>

              {/* Unread dot */}
              {!n.is_read && (
                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <button
        onClick={() => {
          onClose();
          navigate('/notifications');
        }}
        className="block w-full text-center px-4 py-2.5 text-sm text-blue-600 hover:bg-gray-50 border-t border-gray-100 font-medium"
      >
        View all notifications
      </button>
    </div>
  );
}
